import React, { useState } from 'react';

import { climbBridgeAPI } from '../../../lib/api';

const BUTTON = 'px-3 py-1.5 text-xs rounded border border-[#2a2a2a] bg-[#0a0a0a] text-gray-300 hover:border-[#3a3a3a] disabled:opacity-30';

function ago(iso) {
  if (!iso) return '';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  return s < 60 ? `${s}s ago` : `${Math.round(s / 60)} min ago`;
}

/**
 * Team photos on the Live screen: does every team have a picture, and which
 * files in the folder did not match a team cleanly. The point is to find a
 * missing or misnamed photo before the team intro goes to air, not after.
 *
 * Modelled on the timing bridge's rider photos. The difference is that a TBA
 * entry is a pair, so a file can be named after either climber.
 */
export default function PhotosPanel({ showId, photos, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  if (!photos) return null;
  const { folder, fileCount, loadedAt, loading, teams = [], unmatched = [], ambiguous = [], mismatched = [], duplicates = [] } = photos;
  const problems = mismatched.length + ambiguous.length + unmatched.length + duplicates.length;
  const missing = teams.filter(t => !t.photo).length;

  async function reload(nextFolder) {
    setBusy(true);
    setError('');
    try {
      await climbBridgeAPI.reloadPhotos(showId, nextFolder);
      setEditing(false);
      await onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not read the photos folder');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-lg border border-[#2a2a2a] bg-[#111111] p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-white">Team photos</h3>
        <span className="text-[11px] text-gray-500">
          {!folder
            ? 'No folder set'
            : loading
              ? 'Reading the folder…'
              : `${fileCount} picture${fileCount === 1 ? '' : 's'}${loadedAt ? `, read ${ago(loadedAt)}` : ''}${teams.length ? ` · ${teams.length - missing} of ${teams.length} teams` : ''}`}
        </span>
        <div className="ml-auto flex gap-2">
          {folder && <button className={BUTTON} disabled={busy} onClick={() => reload()}>{busy ? 'Reading…' : 'Re-read now'}</button>}
          <button className={BUTTON} disabled={busy} onClick={() => { setDraft(folder || ''); setEditing(e => !e); }}>{folder ? 'Change folder' : 'Add a folder'}</button>
        </div>
      </div>

      {editing && (
        <div className="flex gap-2">
          <input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Google Drive folder link"
            className="flex-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500"
          />
          <button className={BUTTON} disabled={busy} onClick={() => reload(draft.trim())}>Use this folder</button>
        </div>
      )}

      {(photos.error || error) && (
        <p className="text-[11px] text-amber-500">
          {error || photos.error}
          {photos.shareWith && !String(error || photos.error).includes(photos.shareWith) && <> Share it with <code className="select-all text-amber-300">{photos.shareWith}</code>.</>}
        </p>
      )}

      {folder && teams.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {teams.map(t => (
            <div
              key={t.bib}
              className={`flex items-center gap-2 px-2 py-1 rounded border text-xs ${t.photo ? 'border-[#2a2a2a] bg-[#0a0a0a]' : 'border-amber-800 bg-amber-950/30'}`}
              title={t.file ? `${t.file} (matched by ${t.via})` : 'No picture in the folder for this team'}
            >
              {t.photo
                ? <img src={t.photo} alt="" className="w-7 h-7 rounded object-contain bg-[#1a1a1a]" />
                : <span className="w-7 h-7 rounded bg-[#1a1a1a] flex items-center justify-center text-amber-500">?</span>}
              <span className="text-gray-500 tabular-nums">{t.bib}</span>
              <span className="text-gray-200">{t.name}</span>
              {!t.photo && <span className="text-amber-500">no photo</span>}
            </div>
          ))}
        </div>
      )}

      {folder && problems > 0 && (
        <div className="space-y-1 text-[11px]">
          {mismatched.map(m => (
            <p key={m.file} className="text-amber-400">{m.file}: bib {m.bib} is {m.team}. Used for bib {m.bib}; rename it if that is the wrong team.</p>
          ))}
          {ambiguous.map(a => (
            <p key={a.file} className="text-amber-400">{a.file}: could be {a.teams.join(' or ')}. Not used; add the bib to the name.</p>
          ))}
          {duplicates.map(d => (
            <p key={d.ignored} className="text-gray-500">{d.ignored}: another picture for bib {d.bib}; using {d.kept}.</p>
          ))}
          {unmatched.length > 0 && (
            <p className="text-gray-500">Matched nobody: {unmatched.map(u => u.file).join(', ')}</p>
          )}
        </div>
      )}

      <p className="text-[10px] text-gray-600 leading-relaxed">
        Name each file with the team&apos;s bib, either climber&apos;s name, or both — <code>91.png</code>,{' '}
        <code>Ondra.png</code> and <code>91 Ondra Schenk.png</code> all work. Remove the background first:
        the bridge serves each picture exactly as uploaded, so export it at a sensible size.
      </p>
    </section>
  );
}
